import { resolveCatalogImageUrl } from './catalog';
import {
  AUTH_SESSION_CHANGING_EVENT,
  AUTH_SESSION_INVALID_EVENT,
} from './auth';
import {
  getCatalogStorageSession,
  isCatalogStorageSessionCurrent,
  purgeCatalogSessionStorage,
} from './catalogSessionStorage';
import {
  adaptOperationalCatalog,
  fetchOperationalCatalog,
  readOperationalCatalogSnapshot,
  writeOperationalCatalogSnapshot,
} from './waiterOperation';

const activeLoads = new Map();
let listenersInstalled = false;

const loadKeyForSession = (session) => {
  const companyId = String(session?.user?.companyId || '').trim();
  const userId = String(session?.user?.id || session?.user?.email || '').trim();
  const browserSessionId = String(session?.browserSessionId || '').trim();
  return companyId && userId && browserSessionId
    ? `${companyId}:${userId}:${browserSessionId}`
    : '';
};

const abortActiveLoads = () => {
  activeLoads.forEach(({ controller }) => controller?.abort?.());
  activeLoads.clear();
};

export const invalidateOperationalCatalogLoader = ({ purge = false } = {}) => {
  abortActiveLoads();
  if (purge) purgeCatalogSessionStorage();
};

const installInvalidationListeners = () => {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;
  const invalidate = () => invalidateOperationalCatalogLoader();
  window.addEventListener(AUTH_SESSION_CHANGING_EVENT, invalidate);
  window.addEventListener(AUTH_SESSION_INVALID_EVENT, invalidate);
};

const normalizeCatalogProducts = (catalog) => adaptOperationalCatalog(catalog).map((product) => ({
  ...product,
  imageUrl: resolveCatalogImageUrl(product) || product.imageUrl || '',
}));

const conditionalHeaders = (snapshot) => (
  snapshot?.etag ? { 'If-None-Match': snapshot.etag } : {}
);

export const getOperationalCatalogSnapshot = ({
  session = getCatalogStorageSession(),
  now = Date.now(),
} = {}) => readOperationalCatalogSnapshot({ session, now });

export const requestOperationalCatalog = ({
  fetchCatalog,
  session = getCatalogStorageSession(),
  timeoutMs = 20000,
  now = Date.now,
} = {}) => {
  installInvalidationListeners();
  const loadKey = loadKeyForSession(session);
  if (!loadKey || !isCatalogStorageSessionCurrent(session)) {
    return Promise.reject(new Error('Sua sessão mudou. Reabra a tela para atualizar o cardápio.'));
  }
  const existing = activeLoads.get(loadKey);
  if (existing) return existing.promise;

  const snapshot = getOperationalCatalogSnapshot({ session, now: now() });
  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const promise = fetchOperationalCatalog(
    (signal) => fetchCatalog(signal, {
      catalogRevision: snapshot?.catalogRevision ?? '',
      etag: snapshot?.etag || '',
      headers: conditionalHeaders(snapshot),
    }),
    { timeoutMs, controller }
  ).then((catalog) => {
    if (!isCatalogStorageSessionCurrent(session)) {
      throw new Error('Sua sessão mudou. Reabra a tela para atualizar o cardápio.');
    }
    const validatedAt = now();
    if (catalog?.notModified) {
      if (!Array.isArray(snapshot?.products)) {
        throw new Error('O servidor confirmou uma revisão sem snapshot local do cardápio.');
      }
      const result = {
        products: snapshot.products,
        validatedAt,
        catalogRevision: catalog.catalogRevision || snapshot.catalogRevision || '',
        etag: catalog.etag || snapshot.etag || '',
        notModified: true,
      };
      writeOperationalCatalogSnapshot(result.products, {
        session,
        now: validatedAt,
        catalogRevision: result.catalogRevision,
        etag: result.etag,
      });
      return result;
    }

    const products = normalizeCatalogProducts(catalog);
    const result = {
      products,
      validatedAt,
      catalogRevision: catalog?.catalogRevision ?? '',
      etag: catalog?.etag || '',
      notModified: false,
    };
    writeOperationalCatalogSnapshot(products, {
      session,
      now: validatedAt,
      catalogRevision: result.catalogRevision,
      etag: result.etag,
    });
    return result;
  }).finally(() => {
    if (activeLoads.get(loadKey)?.promise === promise) activeLoads.delete(loadKey);
  });

  activeLoads.set(loadKey, { controller, promise });
  return promise;
};

export const prefetchOperationalCatalog = (options) => (
  requestOperationalCatalog(options).catch(() => null)
);

export const scheduleOperationalCatalogPrefetch = (callback, { delayMs = 5000 } = {}) => {
  if (typeof window === 'undefined' || typeof callback !== 'function') return () => {};
  if (navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) {
    return () => {};
  }
  let cancelled = false;
  let idleId = null;
  let timeoutId = null;
  const run = () => {
    if (!cancelled) callback();
  };
  timeoutId = window.setTimeout(() => {
    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      run();
    }
  }, delayMs);
  return () => {
    cancelled = true;
    window.clearTimeout(timeoutId);
    if (idleId !== null && typeof window.cancelIdleCallback === 'function') {
      window.cancelIdleCallback(idleId);
    }
  };
};

export const __resetOperationalCatalogLoaderForTests = () => {
  abortActiveLoads();
};
